import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAppSelector } from '../../../Redux/hooks';
import { selectSlotsByStudent } from '../../../Redux/selectors';
import { useTranslation } from '../../../i18n/useTranslation';
import type { LessonModel } from '../../../Models/LessonModel';
import { LessonStatus } from '../../../Models/enums';
import { displayNameOf } from '../../../Utils/DisplayNameUtil';
import { formatDate } from '../../../Utils/DateUtil';
import { lessonVisualState } from '../../../Utils/LessonStatusUtil';
import { MoneyText } from '../../SharedArea/MoneyText/MoneyText';
import { Spinner } from '../../SharedArea/Spinner/Spinner';
import { ErrorState } from '../../SharedArea/ErrorState/ErrorState';
import { EmptyState } from '../../SharedArea/EmptyState/EmptyState';
import { LessonStatusSheet } from './LessonStatusSheet';
import { QuickAddLesson } from './QuickAddLesson';
import './Lessons.css';
import { useDisplayName } from '../../../Utils/useDisplayName';

type Filter = 'all' | 'owed' | 'paid' | 'scheduled';

/**
 * The lesson ledger. Newest first, because a tutor opening this is looking at
 * what just happened rather than at last winter.
 *
 * The state on each row comes from `lessonVisualState`, so an unpaid lesson
 * that has not happened yet reads as scheduled rather than as a debt.
 */
export function Lessons() {
  const displayName = useDisplayName();
  const { tr, language, direction } = useTranslation();
  const lessons = useAppSelector(state => state.lessons.items);
  const state = useAppSelector(store => store.lessons.state);
  const error = useAppSelector(store => store.lessons.error);
  const slotsByStudent = useAppSelector(selectSlotsByStudent);
  const roster = useAppSelector(store => store.students.items);
  const [searchParams, setSearchParams] = useSearchParams();

  const [filter, setFilter] = useState<Filter>('all');
  const [query, setQuery] = useState('');
  const [editing, setEditing] = useState<LessonModel | null>(null);
  const [adding, setAdding] = useState(searchParams.get('add') === '1');

  useEffect(() => {
    if (searchParams.get('add') === '1') setAdding(true);
  }, [searchParams]);

  function closeAddLesson() {
    setAdding(false);
    if (searchParams.has('add')) {
      const next = new URLSearchParams(searchParams);
      next.delete('add');
      setSearchParams(next, { replace: true });
    }
  }

  const rows = useMemo(() => {
    const now = new Date();
    const needle = query.trim().toLowerCase();
    const displayNameEn = (name: string) => displayNameOf(name, roster, 'en');
    return lessons
      .filter(lesson => {
        // Either spelling, whatever the interface language, like the roster search.
        if (needle && !`${lesson.student} ${displayNameEn(lesson.student)}`.toLowerCase().includes(needle)) return false;
        if (filter === 'all') return true;
        const visual = lessonVisualState(lesson, slotsByStudent.get(lesson.student) ?? [], now);
        if (filter === 'owed') return visual === 'is-unpaid' || visual === 'is-cancelled-charged';
        if (filter === 'paid') return visual === 'is-paid';
        return visual === 'is-scheduled';
      })
      .slice()
      .reverse();
  }, [lessons, filter, query, slotsByStudent, roster]);

  if (state === 'loading' || state === 'idle') return <Spinner />;
  if (state === 'failed') return <ErrorState code={error} />;

  return (
    <section className="lessons" dir={direction}>
      <header className="lessons-header">
        <div className="lessons-heading">
          <span className="lessons-heading-mark" aria-hidden="true" />
          <h1 className="screen-title">{tr('navLessons')}</h1>
        </div>
        <button type="button" className="primary-button" onClick={() => setAdding(true)}>
          <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
          {tr('addLesson')}
        </button>
      </header>

      <div className="lessons-filters">
        <label className="lessons-search">
          <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <circle cx="11" cy="11" r="6.5" stroke="currentColor" strokeWidth="1.8" />
            <path d="m16 16 4 4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
          </svg>
          <input
            type="search"
            className="text-input"
            value={query}
            onChange={event => setQuery(event.target.value)}
            placeholder={tr('search')}
            aria-label={tr('search')}
          />
        </label>
        <div className="filter-chips" role="group" aria-label={tr('navLessons')}>
          {(['all', 'owed', 'paid', 'scheduled'] as Filter[]).map(option => (
            <button
              key={option}
              type="button"
              className={filter === option ? 'filter-chip is-active' : 'filter-chip'}
              onClick={() => setFilter(option)}
              aria-pressed={filter === option}
            >
              {tr(option === 'all' ? 'filterAll' : option === 'owed' ? 'debt' : option === 'paid' ? 'paid' : 'scheduled')}
            </button>
          ))}
        </div>
      </div>

      {rows.length === 0
        ? <EmptyState title={tr('noLessons')} hint={tr('noLessonsHint')} />
        : (
          <ul className="lesson-rows">
            {rows.map(lesson => {
              const visual = lessonVisualState(lesson, slotsByStudent.get(lesson.student) ?? []);
              return (
                <li key={lesson.id} className={`lesson-row ${visual}`}>
                  <button type="button" className="lesson-row-button" onClick={() => setEditing(lesson)}>
                    <span className="lesson-row-avatar" aria-hidden="true">
                      {displayName(lesson.student).trim().charAt(0)}
                    </span>
                    <span className="lesson-row-main">
                      <span className="lesson-row-name">{displayName(lesson.student)}</span>
                      <span className="lesson-row-when">
                        <bdi>{formatDate(lesson.date, language)}</bdi>
                        {lesson.time && <> · <bdi>{lesson.time}</bdi></>}
                      </span>
                    </span>
                    <MoneyText amount={lesson.amount} className="lesson-row-amount" />
                    <span className="lesson-row-status">
                      <span className="lesson-row-status-dot" aria-hidden="true" />
                      <span>{tr(statusLabel(lesson, visual))}</span>
                      <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                        <path d="m8 10 4 4 4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}

      {editing && <LessonStatusSheet lesson={editing} onClose={() => setEditing(null)} />}
      {adding && <QuickAddLesson onClose={closeAddLesson} />}
    </section>
  );
}

function statusLabel(lesson: LessonModel, visual: string) {
  if (visual === 'is-scheduled') return 'scheduled' as const;
  switch (lesson.status) {
    case LessonStatus.Paid: return 'paid' as const;
    case LessonStatus.CancelledCharged: return 'stCancelledCharged' as const;
    case LessonStatus.CancelledFree: return 'stCancelledFree' as const;
    default: return 'unpaid' as const;
  }
}
